import { useState, useEffect, useRef } from 'react';
import { jobsApi, clientsApi, invoicesApi } from '@/lib/api';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, Printer, X, CreditCard } from 'lucide-react';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const invoiceStatuses = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];

// LHS Document Header Component with actual logo
const LHSDocumentHeader = ({ documentType, documentNumber, date, reference }) => (
  <div className="document-header rounded-t-lg">
    <div className="flex justify-between items-start">
      <div className="flex items-center gap-3">
        <img 
          src="https://customer-assets.emergentagent.com/job_park-case-tracker/artifacts/cww8tyf2_LHS%20LOGO%202%20.png" 
          alt="Leisure Home Solutions" 
          className="h-16 w-auto"
        />
      </div>
      <div className="text-right">
        <div className="text-sm opacity-80">{documentType} No:</div>
        <div className="document-number">{documentNumber}</div>
        <div className="text-sm mt-1">Date: {date}</div>
        {reference && <div className="text-sm">Ref: {reference}</div>}
      </div>
    </div>
  </div>
);

// Invoice Preview Component
const InvoicePreview = ({ invoice, job, client, onClose }) => {
  const printRef = useRef();
  
  const handlePrint = () => {
    const content = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <style>
            body { font-family: 'Inter', -apple-system, sans-serif; margin: 0; padding: 20px; }
            .document-header { background: linear-gradient(135deg, #1a1a2e 0%, #1F7A78 70%, #0dbdad 100%); color: white; padding: 24px; border-radius: 8px 8px 0 0; }
            .document-logo { display: flex; align-items: center; gap: 12px; }
            .lhs-logo-text { font-family: 'League Spartan', sans-serif; font-weight: 700; }
            .document-number { font-size: 1.5rem; font-weight: 600; }
            .section-title { color: #1F7A78; font-weight: 600; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.05em; border-bottom: 2px solid #0dbdad; padding-bottom: 4px; margin-bottom: 8px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
            th { background: #1F7A78; color: white; padding: 12px; text-align: left; font-size: 0.75rem; text-transform: uppercase; }
            td { padding: 12px; border-bottom: 1px solid #e5e7eb; }
            .total-row { background: #1F7A78; color: white; font-weight: bold; }
            .payment-box { background: #f9fafb; padding: 16px; border-radius: 8px; border-left: 4px solid #0dbdad; }
          </style>
        </head>
        <body>${content.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="sticky top-0 bg-white z-10 flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">Invoice Preview</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div ref={printRef} className="invoice-template">
          <LHSDocumentHeader 
            documentType="Invoice"
            documentNumber={invoice.invoice_number || 'DRAFT'}
            date={invoice.created_at ? format(new Date(invoice.created_at), 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy')}
            reference={job?.job_number || invoice.reference}
          />
          
          <div className="p-6 space-y-6">
            {/* Invoice To */}
            <div>
              <div className="document-section-title">Invoice To</div>
              <div className="text-sm">
                <p className="font-semibold">{client?.name || invoice.client_name || 'Client Name'}</p>
                {(client?.address || invoice.client_address) && <p>{client?.address || invoice.client_address}</p>}
                {(client?.email || invoice.client_email) && <p>{client?.email || invoice.client_email}</p>}
              </div>
            </div>

            {/* Services Table */}
            <div>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="bg-[#1F7A78] text-white p-3 text-left rounded-tl-md">SERVICE</th>
                    <th className="bg-[#1F7A78] text-white p-3 text-right rounded-tr-md w-32">PRICE</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="p-3 border-b border-gray-200">
                        <div>{item.description}</div>
                        {item.details && <div className="text-xs text-gray-500">{item.details}</div>}
                      </td>
                      <td className="p-3 border-b border-gray-200 text-right">£{(item.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  {invoice.claim_reference && (
                    <tr>
                      <td className="p-3 border-b border-gray-200 text-xs text-gray-500" colSpan={2}>
                        CLAIM REF NUMBER: {invoice.claim_reference}
                        {invoice.policy_number && <span> | POLICY NUMBER: {invoice.policy_number}</span>}
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="p-3 font-bold bg-[#1F7A78] text-white rounded-bl-md">TOTAL DUE</td>
                    <td className="p-3 font-bold bg-[#1F7A78] text-white text-right rounded-br-md">
                      £{(invoice.total || 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Payment Details */}
            <div className="payment-box">
              <div className="document-section-title">Payment To</div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Bank:</strong> Starling Bank</p>
                  <p><strong>Account Name:</strong> Dean Burton</p>
                  <p className="text-xs text-gray-500">(Leisure Home Solutions)</p>
                </div>
                <div>
                  <p><strong>Account Number:</strong> 29747622</p>
                  <p><strong>Sort Code:</strong> 60-83-71</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 text-sm">
                <p><strong>Tel:</strong> 07557 017694</p>
                <p><strong>Email:</strong> deanburton@leisurehomesolutions.co.uk</p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  
  const [formData, setFormData] = useState({
    job_id: '',
    client_id: '',
    client_name: '',
    client_address: '',
    reference: '',
    claim_reference: '',
    policy_number: '',
    items: [{ description: '', details: '', amount: '' }],
    total: 0,
    status: 'draft',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsData, clientsData, invoicesData] = await Promise.all([
        jobsApi.getAll(),
        clientsApi.getAll(),
        invoicesApi.getAll(),
      ]);
      setJobs(jobsData);
      setClients(clientsData);
      setInvoices(invoicesData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      job_id: '', client_id: '', client_name: '', client_address: '', reference: '',
      claim_reference: '', policy_number: '', items: [{ description: '', details: '', amount: '' }], total: 0, status: 'draft',
    });
    setEditingInvoice(null);
  };

  const openDialog = (invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      // Map backend fields to form fields
      setFormData({
        job_id: invoice.case_id || '',
        client_id: invoice.client_id || '',
        client_name: invoice.client_name || '',
        client_address: invoice.client_address || '',
        reference: invoice.reference || '',
        claim_reference: invoice.claim_reference || '',
        policy_number: invoice.policy_number || '',
        items: invoice.items && invoice.items.length > 0 ? invoice.items : [{ description: '', details: '', amount: '' }],
        total: invoice.total || 0,
        status: invoice.status || 'draft',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleJobSelect = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      const client = clients.find(c => c.id === job.client_id);
      setFormData({
        ...formData,
        job_id: jobId,
        client_id: job.client_id || '',
        client_name: client?.name || '',
        client_address: client?.address || '',
        reference: job.job_number,
        claim_reference: job.claim_reference || '',
        policy_number: job.policy_number || '',
        items: [{ 
          description: `${job.job_number} Inspection Report`, 
          details: `${job.property_address}, ${job.property_postcode}`,
          amount: 220 
        }],
        total: 220,
      });
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setFormData({
      ...formData,
      client_id: clientId,
      client_name: client?.name || '',
      client_address: client?.address || '',
    });
  };

  const addLineItem = () => {
    setFormData({ ...formData, items: [...formData.items, { description: '', details: '', amount: '' }] });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: field === 'amount' ? parseFloat(value) || 0 : value };
    const total = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    setFormData({ ...formData, items: newItems, total });
  };

  const removeLineItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const total = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    setFormData({ ...formData, items: newItems, total });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const invoiceData = {
        case_id: formData.job_id || null,
        client_id: formData.client_id || null,
        client_name: formData.client_name,
        client_address: formData.client_address,
        reference: formData.reference,
        items: formData.items.filter(item => item.description || item.amount),
        total: formData.total,
        status: formData.status,
      };
      
      if (editingInvoice) {
        await invoicesApi.update(editingInvoice.id, invoiceData);
        toast.success('Invoice updated');
      } else {
        await invoicesApi.create(invoiceData);
        toast.success('Invoice created');
      }
      
      // Refresh invoices list
      const updatedInvoices = await invoicesApi.getAll();
      setInvoices(updatedInvoices);
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save invoice:', error);
      toast.error('Failed to save invoice');
    }
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm('Delete this invoice?')) return;
    
    try {
      await invoicesApi.delete(invoice.id);
      const updatedInvoices = await invoicesApi.getAll();
      setInvoices(updatedInvoices);
      toast.success('Invoice deleted');
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const handlePayNow = async (invoice) => {
    if (invoice.status === 'paid') {
      toast.info('This invoice has already been paid');
      return;
    }
    
    try {
      toast.loading('Redirecting to payment...');
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/payments/checkout`, {
        invoice_id: invoice.id,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
    }
  };

  const handlePreview = (invoice) => {
    setSelectedInvoice(invoice);
    setIsPreviewOpen(true);
  };

  const getJobInfo = (jobId) => jobs.find(j => j.id === jobId);
  const getClientInfo = (clientId) => clients.find(c => c.id === clientId);

  const columns = [
    { key: 'invoice_number', label: 'Invoice #' },
    { key: 'reference', label: 'Ref', render: (val) => val || '-' },
    { key: 'client_name', label: 'Client', render: (val, row) => val || getClientInfo(row.client_id)?.name || '-' },
    { key: 'items', label: 'Services', render: (val) => val?.length ? `${val.length} item(s)` : '-' },
    { key: 'total', label: 'Total', render: (val) => `£${(val || 0).toFixed(2)}` },
    { key: 'status', label: 'Status', render: (val) => (
      <Badge className={
        val === 'paid' ? 'bg-green-100 text-green-800' :
        val === 'sent' ? 'bg-blue-100 text-blue-800' :
        val === 'overdue' ? 'bg-red-100 text-red-800' :
        'bg-gray-100 text-gray-600'
      }>{val}</Badge>
    )},
    { key: 'created_at', label: 'Date', render: (val) => val ? format(new Date(val), 'dd/MM/yyyy') : '-' },
  ];

  const actions = [
    { label: 'Preview', icon: Eye, onClick: handlePreview },
    { label: 'Pay Now', icon: CreditCard, onClick: handlePayNow, highlight: true },
    { label: 'Edit', icon: Pencil, onClick: openDialog },
    { label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true },
  ];

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage invoices</p>
        </div>
        <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-invoice-btn">
          <Plus className="h-4 w-4 mr-2" /> New Invoice
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={invoices}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No invoices found. Create your first invoice!"
      />

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>{editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Quick Fill from Job */}
            <div className="space-y-2">
              <Label>Quick Fill from Job</Label>
              <Select value={formData.job_id} onValueChange={handleJobSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select job to auto-fill details" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Manual entry</SelectItem>
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.job_number} - {j.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={formData.client_id} onValueChange={handleClientSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {invoiceStatuses.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Client Name (for invoice)</Label>
              <Input
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Client Address</Label>
              <Textarea
                value={formData.client_address}
                onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Claim Reference</Label>
                <Input
                  value={formData.claim_reference}
                  onChange={(e) => setFormData({ ...formData, claim_reference: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Policy Number</Label>
                <Input
                  value={formData.policy_number}
                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                />
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Services / Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                  <Plus className="h-3 w-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {formData.items.map((item, idx) => (
                  <div key={idx} className="space-y-2 p-3 bg-gray-50 rounded-lg">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Service description"
                          value={item.description}
                          onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                        />
                        <Input
                          placeholder="Details (optional)"
                          value={item.details || ''}
                          onChange={(e) => updateLineItem(idx, 'details', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="£"
                        value={item.amount || ''}
                        onChange={(e) => updateLineItem(idx, 'amount', e.target.value)}
                        className="w-28"
                      />
                      {formData.items.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeLineItem(idx)}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="bg-[#1F7A78] text-white p-4 rounded-lg flex justify-between items-center">
              <span className="font-semibold">TOTAL DUE</span>
              <span className="text-2xl font-bold">£{formData.total.toFixed(2)}</span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary">
                {editingInvoice ? 'Update' : 'Create'} Invoice
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      {isPreviewOpen && selectedInvoice && (
        <InvoicePreview 
          invoice={selectedInvoice}
          job={getJobInfo(selectedInvoice.job_id)}
          client={getClientInfo(selectedInvoice.client_id)}
          onClose={() => setIsPreviewOpen(false)} 
        />
      )}
    </div>
  );
}
